import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowRightIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  XMarkIcon,
  TrashIcon,
  UserIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  EnvelopeIcon,
  PlusIcon,
  BriefcaseIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import {
  OnboardingRequestsClient,
  TemplatesClient,
  LocationsClient,
  DepartmentsClient,
  RequestItemsClient,
  TemplateTasksClient,
  InitializeOnboardingRequestCommand,
  ApplyTemplateRequest,
  AddCustomRequestItemCommand,
  LocationDto,
  DepartmentDto,
  TemplateDto,
  TemplateTaskListDto,
  OnboardingRequestPreviewDto,
  EmployeePreviewDto,
  RequestItemCategoryDto,
  RequestItemPreviewDto,
  RequestType,
  TeamDto,
  UpdateRequestItemPicRequest
} from '../../web-api-client';
import { toast } from 'sonner';
import { API_CONFIG } from '../../config/api';
import { usersService, User } from '../../services/usersService';
import { teamsService } from '../../services/teamsService';
import { createAuthenticatedFetch } from '../../utils/apiClient';
import AddTaskModal, { AddTaskData } from '../../components/checklist/AddTaskModal';
import { getErrorMessage } from '../../utils/errorHelpers';

interface NewOnboardingRequestFormProps {
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function NewOnboardingRequestForm({ show, onClose, onSuccess }: NewOnboardingRequestFormProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [locations, setLocations] = useState<LocationDto[]>([]);
  const [departments, setDepartments] = useState<DepartmentDto[]>([]);
  const [templates, setTemplates] = useState<TemplateDto[]>([]);
  const [preview, setPreview] = useState<OnboardingRequestPreviewDto | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [teams, setTeams] = useState<TeamDto[]>([]);
  const [availableTemplateTasks, setAvailableTemplateTasks] = useState<TemplateTaskListDto[]>([]);
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [tempIdCounter, setTempIdCounter] = useState<number>(1);
  const [approverEmailErrors, setApproverEmailErrors] = useState<{ [key: number]: string }>({});
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  const [formData, setFormData] = useState({
    employeeName: '',
    locationId: 0,
    departmentId: 0,
    designation: '',
    personalEmail: '',
    immediateSuperiorEmail: '',
    reportingSuperiorEmail: '',
    employmentDate: ''
  });

  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [draftRequestId, setDraftRequestId] = useState<number | null>(null);

  // Email validation helper
  const isValidEmail = (email: string): boolean => {
    if (!email.trim()) return true; // Empty is valid (for optional fields)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Clear field error when user starts typing
  const handleFieldChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear the error for this field when user starts correcting
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Fetch locations when modal opens
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const client = new LocationsClient(API_CONFIG.baseUrl, createAuthenticatedFetch());
        const data = await client.getLocations();
        setLocations(data);
      } catch (error) {
        console.error('Failed to fetch locations:', error);
        toast.error('Failed to load locations');
      }
    };

    if (show) {
      fetchLocations();
    }
  }, [show]);

  // Fetch departments when location changes
  useEffect(() => {
    const fetchDepartments = async () => {
      if (formData.locationId > 0) {
        try {
          const client = new DepartmentsClient(API_CONFIG.baseUrl, createAuthenticatedFetch());
          const data = await client.getDepartments(formData.locationId);
          setDepartments(data);
        } catch (error) {
          console.error('Failed to fetch departments:', error);
          toast.error('Failed to load departments');
        }
      } else {
        setDepartments([]);
      }
    };

    fetchDepartments();
  }, [formData.locationId]);

  // Fetch templates when step 2 is reached
  useEffect(() => {
    const fetchTemplates = async () => {
      if (currentStep === 2) {
        try {
          const client = new TemplatesClient(API_CONFIG.baseUrl, createAuthenticatedFetch());
          const data = await client.getTemplates(RequestType.Onboarding);
          setTemplates(data);
        } catch (error) {
          console.error('Failed to fetch templates:', error);
          toast.error('Failed to load templates');
        }
      }
    };

    fetchTemplates();
  }, [currentStep]);

  // Generate preview when step 3 is reached
  useEffect(() => {
    const generatePreview = async () => {
      if (currentStep === 3 && selectedTemplateId && !preview) {
        try {
          // Fetch the full template with categories and tasks
          const templatesClient = new TemplatesClient(API_CONFIG.baseUrl, createAuthenticatedFetch());
          const allTemplates = await templatesClient.getTemplates(RequestType.Onboarding);
          const selectedTemplate = allTemplates.find(t => t.id === selectedTemplateId);

          if (!selectedTemplate) {
            toast.error('Template not found');
            return;
          }

          // Get location and department names
          const location = locations.find(l => l.id === formData.locationId);
          const department = departments.find(d => d.id === formData.departmentId);

          // Build a client-side preview (not saved to database yet)
          const employeePreview = new EmployeePreviewDto({
            fullName: formData.employeeName,
            locationName: location?.name,
            departmentName: department?.name,
            designation: formData.designation,
            personalEmail: formData.personalEmail,
            immediateSuperiorEmail: formData.immediateSuperiorEmail || undefined,
            reportingSuperiorEmail: formData.reportingSuperiorEmail || undefined,
            employmentDate: new Date(formData.employmentDate)
          });

          let tempCounter = 1;
          const categories = selectedTemplate.categories?.map(category => {
            const items = category.tasks?.map(task => {
              // Generate unique temporary ID for each task
              const tempId = -(Date.now() + tempCounter);
              tempCounter++;

              // Find the user for the defaultPicId
              const defaultUser = users.find(u => u.id === task.defaultPicId);
              // Find the team for the defaultTeamId
              const defaultTeam = teams.find(t => t.id === task.defaultTeamId);

              return new RequestItemPreviewDto({
                id: tempId, // Unique temporary ID
                itemName: task.taskName,
                description: task.description,
                picId: task.defaultPicId,
                assignedPersonName: defaultUser?.userName,
                teamId: task.defaultTeamId,
                assignedTeamName: defaultTeam?.name,
                requiresApproval: task.requiresApproval || false,
                approverEmail: task.approverEmail
              });
            });

            return new RequestItemCategoryDto({
              categoryId: category.id,
              categoryName: category.name,
              items: items
            });
          });

          const clientPreview = new OnboardingRequestPreviewDto({
            employee: employeePreview,
            categories: categories
          });

          setPreview(clientPreview);
        } catch (error) {
          console.error('Failed to generate preview:', error);
          toast.error('Failed to generate preview');
        }
      }
    };

    generatePreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, selectedTemplateId]);

  const handleStep1Submit = async () => {
    // Frontend validation - collect all errors at once for better UX
    const errors: { [key: string]: string } = {};

    if (!formData.employeeName.trim()) {
      errors.employeeName = 'Please enter employee name';
    }
    if (formData.locationId === 0) {
      errors.locationId = 'Please select a location';
    }
    if (formData.departmentId === 0) {
      errors.departmentId = 'Please select a department';
    }
    if (!formData.designation.trim()) {
      errors.designation = 'Please enter designation';
    }
    if (!formData.personalEmail.trim()) {
      errors.personalEmail = 'Please enter personal email';
    } else if (!isValidEmail(formData.personalEmail)) {
      errors.personalEmail = 'Please enter a valid email address (e.g., name@example.com)';
    }
    if (!formData.employmentDate) {
      errors.employmentDate = 'Please select employment date';
    }
    if (!formData.immediateSuperiorEmail.trim()) {
      errors.immediateSuperiorEmail = 'Please enter immediate superior email';
    } else if (!isValidEmail(formData.immediateSuperiorEmail)) {
      errors.immediateSuperiorEmail = 'Please enter a valid email address (e.g., name@example.com)';
    }
    // Reporting superior email is optional, but must be valid format if provided
    if (formData.reportingSuperiorEmail.trim() && !isValidEmail(formData.reportingSuperiorEmail)) {
      errors.reportingSuperiorEmail = 'Please enter a valid email address (e.g., name@example.com)';
    }

    // If there are validation errors, show them inline and stop
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error('Please correct the highlighted fields');
      return;
    }

    // Clear any previous errors
    setFormErrors({});

    // Check if email already exists
    try {
      const response = await fetch(`${API_CONFIG.baseUrl}/api/employees/check-email?email=${encodeURIComponent(formData.personalEmail)}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.exists) {
          toast.error('This personal email is already registered in the system');
          return;
        }
      }
    } catch (error) {
      console.error('Failed to check email:', error);
      toast.error('Failed to validate email. Please try again.');
      return;
    }

    // Just move to next step - data stays in React state
    setCurrentStep(2);
  };

  const handleStep2Submit = async () => {
    // Frontend validation only - don't save to database yet
    if (!selectedTemplateId) {
      toast.error('Please select a template');
      return;
    }

    // Fetch users, teams, and template tasks for step 3
    try {
      const [usersData, teamsData, templateTasksData] = await Promise.all([
        usersService.getUsers(),
        teamsService.getTeams(),
        new TemplateTasksClient(API_CONFIG.baseUrl, createAuthenticatedFetch()).getTemplateTasks(null, null, true)
      ]);

      setUsers(usersData);
      setTeams(teamsData);
      setAvailableTemplateTasks(templateTasksData);
    } catch (error) {
      console.error('Failed to load users, teams, or template tasks:', error);
      toast.error('Failed to load additional data');
    }

    // Move to next step - draft will be auto-created in Step 3
    setCurrentStep(3);
  };

  const handleAddTaskFromModal = async (taskData: AddTaskData): Promise<void> => {
    if (!preview) return;

    // Generate unique temporary ID using negative numbers and counter
    const tempId = -(Date.now() + tempIdCounter);
    setTempIdCounter(prev => prev + 1);

    // Add task to the appropriate category or uncategorized
    const newItem = new RequestItemPreviewDto({
      id: tempId, // Unique temporary ID for client-side
      itemName: taskData.taskName,
      description: taskData.description,
      requiresApproval: false,
      templateTaskId: taskData.templateTaskId
    });

    if (!taskData.categoryId) {
      toast.error('Cannot add task without a category');
      return;
    }

    // Add to existing category or create new one
    const existingCategory = preview.categories?.find(
      cat => cat.categoryId === taskData.categoryId
    );

    if (existingCategory) {
      const updatedCategories = preview.categories?.map(cat => {
        if (cat.categoryId === taskData.categoryId) {
          return new RequestItemCategoryDto({
            ...cat,
            items: [...(cat.items || []), newItem]
          });
        }
        return cat;
      });

      setPreview(new OnboardingRequestPreviewDto({
        ...preview,
        categories: updatedCategories
      }));
    } else {
      // Create new category
      const newCategory = new RequestItemCategoryDto({
        categoryId: taskData.categoryId,
        categoryName: taskData.categoryName,
        items: [newItem]
      });

      setPreview(new OnboardingRequestPreviewDto({
        ...preview,
        categories: [...(preview.categories || []), newCategory]
      }));
    }
  };

  const handleUpdatePIC = (taskId: number, categoryId: number | undefined, newPicId: string) => {
    if (!preview) return;

    // Determine if it's a team or person assignment
    let actualPicId: string | undefined = undefined;
    let actualTeamId: number | undefined = undefined;
    let assignedPersonName: string | undefined = undefined;
    let assignedTeamName: string | undefined = undefined;

    if (newPicId.startsWith('team-')) {
      // Team assignment
      const teamIdStr = newPicId.replace('team-', '');
      actualTeamId = parseInt(teamIdStr);
      const selectedTeam = teams.find(t => t.id === actualTeamId);
      assignedTeamName = selectedTeam?.name;
    } else if (newPicId.startsWith('person-')) {
      // Person assignment
      actualPicId = newPicId.replace('person-', '');
      const selectedUser = users.find(u => u.id === actualPicId);
      assignedPersonName = selectedUser?.userName;
    } else if (newPicId) {
      // Legacy format - assume it's a person ID
      actualPicId = newPicId;
      const selectedUser = users.find(u => u.id === actualPicId);
      assignedPersonName = selectedUser?.userName;
    }

    if (categoryId) {
      // Update task in category
      const updatedCategories = preview.categories?.map(cat => {
        if (cat.categoryId === categoryId) {
          return new RequestItemCategoryDto({
            ...cat,
            items: cat.items?.map(item => {
              if (item.id === taskId) {
                return new RequestItemPreviewDto({
                  ...item,
                  picId: actualPicId,
                  teamId: actualTeamId,
                  assignedPersonName: assignedPersonName,
                  assignedTeamName: assignedTeamName
                });
              }
              return item;
            })
          });
        }
        return cat;
      });

      setPreview(new OnboardingRequestPreviewDto({
        ...preview,
        categories: updatedCategories
      }));
      toast.success('Assignment updated');
    }
  };

  const handleUpdateItemApproval = (taskId: number, categoryId: number | undefined, requiresApproval: boolean, approverEmail?: string) => {
    if (!preview) return;

    // Validate email format if requires approval
    if (requiresApproval && approverEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(approverEmail)) {
        setApproverEmailErrors(prev => ({
          ...prev,
          [taskId]: 'Please enter a valid email address'
        }));
      } else {
        // Clear error if email is valid
        setApproverEmailErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[taskId];
          return newErrors;
        });
      }
    } else if (!requiresApproval) {
      // Clear error if approval is not required
      setApproverEmailErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[taskId];
        return newErrors;
      });
    } else if (requiresApproval && !approverEmail) {
      // Show error if approval is required but email is empty
      setApproverEmailErrors(prev => ({
        ...prev,
        [taskId]: 'Approver email is required'
      }));
    }

    if (categoryId) {
      // Update task in category
      const updatedCategories = preview.categories?.map(cat => {
        if (cat.categoryId === categoryId) {
          return new RequestItemCategoryDto({
            ...cat,
            items: cat.items?.map(item => {
              if (item.id === taskId) {
                return new RequestItemPreviewDto({
                  ...item,
                  requiresApproval: requiresApproval,
                  approverEmail: requiresApproval ? (approverEmail || '') : undefined
                });
              }
              return item;
            })
          });
        }
        return cat;
      });

      setPreview(new OnboardingRequestPreviewDto({
        ...preview,
        categories: updatedCategories
      }));
    }
  };

  const handleRemoveTask = (categoryId: number | undefined, taskId: number) => {
    if (!preview) return;

    // Remove from category
    const updatedCategories = preview.categories?.map(cat => {
      if (cat.categoryId === categoryId) {
        return new RequestItemCategoryDto({
          ...cat,
          items: cat.items?.filter(item => item.id !== taskId)
        });
      }
      return cat;
    });

    const updatedPreview = new OnboardingRequestPreviewDto({
      ...preview,
      categories: updatedCategories
    });

    setPreview(updatedPreview);

    toast.success('Task removed from preview');
  };

  const handleSaveDraft = async () => {
    setSubmitting(true);
    try {
      const onboardingClient = new OnboardingRequestsClient(API_CONFIG.baseUrl, createAuthenticatedFetch());

      // Use existing draft or create new one
      let requestId: number;
      if (draftRequestId) {
        requestId = draftRequestId;
      } else {
        // Create draft employee and boarding request
        const initCommand = new InitializeOnboardingRequestCommand({
          employeeName: formData.employeeName,
          locationId: formData.locationId,
          departmentId: formData.departmentId,
          designation: formData.designation,
          personalEmail: formData.personalEmail,
          immediateSuperiorEmail: formData.immediateSuperiorEmail || undefined,
          reportingSuperiorEmail: formData.reportingSuperiorEmail || undefined,
          employmentDate: new Date(formData.employmentDate)
        });

        requestId = await onboardingClient.initializeOnboardingRequest(initCommand);
        setDraftRequestId(requestId);

        // Apply template on first save only
        if (selectedTemplateId) {
          await onboardingClient.applyTemplate(requestId, new ApplyTemplateRequest({ templateId: selectedTemplateId }));
        }
      }

      // Fetch actual request to compare
      const actualRequest = await onboardingClient.getOnboardingRequestPreview(requestId);
      const itemsClient = new RequestItemsClient(API_CONFIG.baseUrl, createAuthenticatedFetch());

      // Get all preview item IDs for comparison
      const previewItemIds = new Set<number>();
      if (preview?.categories) {
        for (const previewCategory of preview.categories) {
          if (previewCategory.items) {
            for (const item of previewCategory.items) {
              // Only track positive IDs (existing items)
              if (item.id && item.id > 0) {
                previewItemIds.add(item.id);
              }
            }
          }
        }
      }

      // Delete items that exist in actual but not in preview
      if (actualRequest.categories) {
        for (const actualCategory of actualRequest.categories) {
          if (actualCategory.items) {
            for (const actualItem of actualCategory.items) {
              if (actualItem.id && !previewItemIds.has(actualItem.id)) {
                await itemsClient.removeRequestItem(actualItem.id);
              }
            }
          }
        }
      }

      // Add new items and update existing ones
      if (preview?.categories) {
        for (const category of preview.categories) {
          if (category.items) {
            for (const item of category.items) {
              // Check if this is a new item (negative ID)
              if (item.id && item.id < 0) {
                // Create new custom request item
                await itemsClient.addCustomRequestItem(new AddCustomRequestItemCommand({
                  boardingRequestId: requestId,
                  itemName: item.itemName,
                  description: item.description,
                  picId: item.picId,
                  teamId: item.teamId,
                  requiresApproval: item.requiresApproval || false,
                  approverEmail: item.approverEmail,
                  templateCategoryId: category.categoryId
                }));
              } else if (item.id && item.id > 0 && (item.picId || item.teamId)) {
                // Update existing items (positive IDs)
                await itemsClient.updateRequestItemPic(item.id, new UpdateRequestItemPicRequest({
                  picId: item.picId,
                  teamId: item.teamId,
                  requiresApproval: item.requiresApproval,
                  approverEmail: item.approverEmail
                }));
              }
            }
          }
        }
      }

      // Reload preview to get actual IDs
      const updatedPreview = await onboardingClient.getOnboardingRequestPreview(requestId);
      setPreview(updatedPreview);

      // Close modal and reset
      resetForm();
      onClose();
      onSuccess();
      toast.success('Draft saved successfully!');
    } catch (error: unknown) {
      console.error('Failed to save draft:', error);
      toast.error(getErrorMessage(error, 'Failed to save draft'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleFinalSubmit = async () => {
    // Validate all items with requires approval have valid emails
    const allItems = preview?.categories?.flatMap(cat => cat.items || []) || [];

    const invalidItems = allItems.filter(item =>
      item.requiresApproval && (!item.approverEmail?.trim() || approverEmailErrors[item.id!])
    );

    if (invalidItems.length > 0) {
      toast.error('Please provide valid approver emails for all items marked as "Requires Approval"');
      return;
    }

    setSubmitting(true);

    try {
      const onboardingClient = new OnboardingRequestsClient(API_CONFIG.baseUrl, createAuthenticatedFetch());

      // Use existing draft or create new one
      let requestId: number;
      if (draftRequestId) {
        requestId = draftRequestId;
      } else {
        // Create draft employee and boarding request
        const initCommand = new InitializeOnboardingRequestCommand({
          employeeName: formData.employeeName,
          locationId: formData.locationId,
          departmentId: formData.departmentId,
          designation: formData.designation,
          personalEmail: formData.personalEmail,
          immediateSuperiorEmail: formData.immediateSuperiorEmail || undefined,
          reportingSuperiorEmail: formData.reportingSuperiorEmail || undefined,
          employmentDate: new Date(formData.employmentDate)
        });

        requestId = await onboardingClient.initializeOnboardingRequest(initCommand);
        setDraftRequestId(requestId);

        // Apply template on first create only
        if (selectedTemplateId) {
          await onboardingClient.applyTemplate(requestId, new ApplyTemplateRequest({ templateId: selectedTemplateId }));
        }
      }

      // Fetch actual request to compare
      const actualRequest = await onboardingClient.getOnboardingRequestPreview(requestId);
      const itemsClient = new RequestItemsClient(API_CONFIG.baseUrl, createAuthenticatedFetch());

      // Get all preview item IDs for comparison
      const previewItemIds = new Set<number>();
      if (preview?.categories) {
        for (const previewCategory of preview.categories) {
          if (previewCategory.items) {
            for (const item of previewCategory.items) {
              // Only track positive IDs (existing items)
              if (item.id && item.id > 0) {
                previewItemIds.add(item.id);
              }
            }
          }
        }
      }

      // Delete items that exist in actual but not in preview
      if (actualRequest.categories) {
        for (const actualCategory of actualRequest.categories) {
          if (actualCategory.items) {
            for (const actualItem of actualCategory.items) {
              if (actualItem.id && !previewItemIds.has(actualItem.id)) {
                await itemsClient.removeRequestItem(actualItem.id);
              }
            }
          }
        }
      }

      // Add new items and update existing ones
      if (preview?.categories) {
        for (const category of preview.categories) {
          if (category.items) {
            for (const item of category.items) {
              // Check if this is a new item (negative ID)
              if (item.id && item.id < 0) {
                // Create new custom request item
                await itemsClient.addCustomRequestItem(new AddCustomRequestItemCommand({
                  boardingRequestId: requestId,
                  itemName: item.itemName,
                  description: item.description,
                  picId: item.picId,
                  teamId: item.teamId,
                  requiresApproval: item.requiresApproval || false,
                  approverEmail: item.approverEmail,
                  templateCategoryId: category.categoryId
                }));
              } else if (item.id && item.id > 0 && (item.picId || item.teamId)) {
                // Update existing items (positive IDs)
                await itemsClient.updateRequestItemPic(item.id, new UpdateRequestItemPicRequest({
                  picId: item.picId,
                  teamId: item.teamId,
                  requiresApproval: item.requiresApproval,
                  approverEmail: item.approverEmail
                }));
              }
            }
          }
        }
      }

      // Submit the request
      await onboardingClient.submitOnboardingRequest(requestId);

      // Reset and close modal
      resetForm();
      onClose();
      onSuccess();
      toast.success('Onboarding request created successfully!');
    } catch (error: unknown) {
      console.error('Failed to create onboarding request:', error);
      toast.error(getErrorMessage(error, 'Failed to create onboarding request'));
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setCurrentStep(1);
    setSelectedTemplateId(null);
    setPreview(null);
    setShowAddTaskModal(false);
    setTempIdCounter(1);
    setApproverEmailErrors({});
    setFormErrors({});
    setDraftRequestId(null);
    setFormData({
      employeeName: '',
      locationId: 0,
      departmentId: 0,
      designation: '',
      personalEmail: '',
      immediateSuperiorEmail: '',
      reportingSuperiorEmail: '',
      employmentDate: ''
    });
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  if (!show) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div
        className="bg-white rounded-2xl shadow-xl max-w-3xl w-full p-6 relative z-50 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">New Onboarding Request</h2>
            <p className="text-sm text-gray-600 mt-1">Step {currentStep} of 3</p>
          </div>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center mb-8">
          <div className={`flex-1 h-2 rounded-full ${currentStep >= 1 ? 'bg-green-600' : 'bg-gray-200'}`} />
          <div className={`flex-1 h-2 rounded-full ml-2 ${currentStep >= 2 ? 'bg-green-600' : 'bg-gray-200'}`} />
          <div className={`flex-1 h-2 rounded-full ml-2 ${currentStep >= 3 ? 'bg-green-600' : 'bg-gray-200'}`} />
        </div>

        {/* Step 1: Employee Information */}
        {currentStep === 1 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Employee Information</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="employeeName" className="block text-sm font-medium text-gray-700 mb-1">
                  Employee Name *
                </label>
                <input
                  type="text"
                  id="employeeName"
                  required
                  value={formData.employeeName}
                  onChange={(e) => handleFieldChange('employeeName', e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                    formErrors.employeeName
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:ring-green-500'
                  }`}
                  placeholder="Full name"
                />
                {formErrors.employeeName && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.employeeName}</p>
                )}
              </div>

              <div>
                <label htmlFor="locationId" className="block text-sm font-medium text-gray-700 mb-1">
                  Location *
                </label>
                <select
                  id="locationId"
                  required
                  value={formData.locationId}
                  onChange={(e) => {
                    handleFieldChange('locationId', Number(e.target.value));
                    setFormData(prev => ({ ...prev, locationId: Number(e.target.value), departmentId: 0 }));
                  }}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                    formErrors.locationId
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:ring-green-500'
                  }`}
                >
                  <option value={0}>Select a location...</option>
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
                {formErrors.locationId && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.locationId}</p>
                )}
              </div>

              <div>
                <label htmlFor="departmentId" className="block text-sm font-medium text-gray-700 mb-1">
                  Department *
                </label>
                <select
                  id="departmentId"
                  required
                  value={formData.departmentId}
                  onChange={(e) => handleFieldChange('departmentId', Number(e.target.value))}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                    formErrors.departmentId
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:ring-green-500'
                  }`}
                  disabled={formData.locationId === 0}
                >
                  <option value={0}>Select a department...</option>
                  {departments.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
                {formErrors.departmentId && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.departmentId}</p>
                )}
              </div>

              <div>
                <label htmlFor="designation" className="block text-sm font-medium text-gray-700 mb-1">
                  Designation *
                </label>
                <input
                  type="text"
                  id="designation"
                  required
                  value={formData.designation}
                  onChange={(e) => handleFieldChange('designation', e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                    formErrors.designation
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:ring-green-500'
                  }`}
                  placeholder="Job title"
                />
                {formErrors.designation && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.designation}</p>
                )}
              </div>

              <div>
                <label htmlFor="personalEmail" className="block text-sm font-medium text-gray-700 mb-1">
                  Personal Email *
                </label>
                <input
                  type="email"
                  id="personalEmail"
                  required
                  value={formData.personalEmail}
                  onChange={(e) => handleFieldChange('personalEmail', e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                    formErrors.personalEmail
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:ring-green-500'
                  }`}
                  placeholder="personal@email.com"
                />
                {formErrors.personalEmail && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.personalEmail}</p>
                )}
              </div>

              <div>
                <label htmlFor="employmentDate" className="block text-sm font-medium text-gray-700 mb-1">
                  Employment Date *
                </label>
                <input
                  type="date"
                  id="employmentDate"
                  required
                  value={formData.employmentDate}
                  onChange={(e) => handleFieldChange('employmentDate', e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                    formErrors.employmentDate
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:ring-green-500'
                  }`}
                />
                {formErrors.employmentDate && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.employmentDate}</p>
                )}
              </div>

              <div>
                <label htmlFor="immediateSuperiorEmail" className="flex items-center text-sm font-medium text-gray-700 mb-1">
                  Immediate Superior Email <span className="text-red-500 ml-1">*</span>
                  <span className="relative group ml-1">
                    <InformationCircleIcon className="h-4 w-4 text-gray-400" />
                    <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                      First Level Approver (Required)
                    </span>
                  </span>
                </label>
                <input
                  type="email"
                  id="immediateSuperiorEmail"
                  required
                  value={formData.immediateSuperiorEmail}
                  onChange={(e) => handleFieldChange('immediateSuperiorEmail', e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                    formErrors.immediateSuperiorEmail
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:ring-green-500'
                  }`}
                  placeholder="supervisor@company.com"
                />
                {formErrors.immediateSuperiorEmail && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.immediateSuperiorEmail}</p>
                )}
              </div>

              <div>
                <label htmlFor="reportingSuperiorEmail" className="flex items-center text-sm font-medium text-gray-700 mb-1">
                  Second Level Approver Email
                  <span className="relative group ml-1">
                    <InformationCircleIcon className="h-4 w-4 text-gray-400" />
                    <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                      Second Level Approver (Optional)
                    </span>
                  </span>
                </label>
                <input
                  type="email"
                  id="reportingSuperiorEmail"
                  value={formData.reportingSuperiorEmail}
                  onChange={(e) => handleFieldChange('reportingSuperiorEmail', e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                    formErrors.reportingSuperiorEmail
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:ring-green-500'
                  }`}
                  placeholder="manager@company.com"
                />
                {formErrors.reportingSuperiorEmail && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.reportingSuperiorEmail}</p>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6 pt-6 border-t">
              <button
                type="button"
                onClick={handleCancel}
                className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleStep1Submit}
                disabled={submitting}
                className="px-6 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
              >
                {submitting ? 'Creating...' : (
                  <>
                    Next
                    <ArrowRightIcon className="ml-2 h-4 w-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Select Template */}
        {currentStep === 2 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Onboarding Template</h3>

            {templates.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">No templates available</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {templates.map((template) => (
                  <div
                    key={template.id}
                    onClick={() => setSelectedTemplateId(template.id!)}
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                      selectedTemplateId === template.id
                        ? 'border-green-600 bg-green-50'
                        : 'border-gray-200 hover:border-green-300'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{template.templateName}</h4>
                        {template.description && (
                          <p className="text-sm text-gray-600 mt-1">{template.description}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-2">
                          {template.categories?.reduce((total, category) => total + (category.tasks?.length || 0), 0) || 0} tasks
                        </p>
                      </div>
                      {selectedTemplateId === template.id && (
                        <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0 ml-2" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-between space-x-3 mt-6 pt-6 border-t">
              <button
                type="button"
                onClick={() => setCurrentStep(1)}
                className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 inline-flex items-center"
                disabled={submitting}
              >
                <ArrowLeftIcon className="mr-2 h-4 w-4" />
                Back
              </button>
              <button
                type="button"
                onClick={handleStep2Submit}
                disabled={submitting || !selectedTemplateId}
                className="px-6 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
              >
                {submitting ? 'Applying...' : (
                  <>
                    Next
                    <ArrowRightIcon className="ml-2 h-4 w-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Preview & Submit */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Review & Customize</h3>
              {preview && (
                <button
                  type="button"
                  onClick={() => setShowAddTaskModal(true)}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Add More
                </button>
              )}
            </div>

            {preview ? (
              <>
                {/* Employee Profile */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <UserIcon className="h-5 w-5 mr-2 text-gray-600" />
                    New Employee Profile
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div className="md:col-span-2">
                      <span className="text-gray-600">Name:</span>
                      <span className="ml-2 font-medium text-gray-900">{preview.employee?.fullName}</span>
                    </div>
                    <div className="flex items-center">
                      <BuildingOfficeIcon className="h-4 w-4 text-gray-400 mr-1" />
                      <span className="text-gray-600">Location:</span>
                      <span className="ml-2 font-medium text-gray-900">{preview.employee?.locationName}</span>
                    </div>
                    <div className="flex items-center">
                      <BuildingOfficeIcon className="h-4 w-4 text-gray-400 mr-1" />
                      <span className="text-gray-600">Department:</span>
                      <span className="ml-2 font-medium text-gray-900">{preview.employee?.departmentName}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Designation:</span>
                      <span className="ml-2 font-medium text-gray-900">{preview.employee?.designation}</span>
                    </div>
                    <div className="flex items-center">
                      <CalendarIcon className="h-4 w-4 text-gray-400 mr-1" />
                      <span className="text-gray-600">Start Date:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {preview.employee?.employmentDate ? new Date(preview.employee.employmentDate + 'Z').toLocaleDateString() : '-'}
                      </span>
                    </div>
                    <div className="flex items-center md:col-span-2">
                      <EnvelopeIcon className="h-4 w-4 text-gray-400 mr-1" />
                      <span className="text-gray-600">Personal Email:</span>
                      <span className="ml-2 font-medium text-gray-900">{preview.employee?.personalEmail}</span>
                    </div>
                    {preview.employee?.immediateSuperiorEmail && (
                      <div className="flex items-center md:col-span-2">
                        <EnvelopeIcon className="h-4 w-4 text-gray-400 mr-1" />
                        <span className="text-gray-600">Immediate Superior:</span>
                        <span className="ml-2 font-medium text-gray-900">{preview.employee.immediateSuperiorEmail}</span>
                      </div>
                    )}
                    {preview.employee?.reportingSuperiorEmail && (
                      <div className="flex items-center md:col-span-2">
                        <EnvelopeIcon className="h-4 w-4 text-gray-400 mr-1" />
                        <span className="text-gray-600">Second Level Approver:</span>
                        <span className="ml-2 font-medium text-gray-900">{preview.employee.reportingSuperiorEmail}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Onboarding Tasks */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Onboarding Tasks</h4>

                  {/* Categories */}
                  {preview.categories && preview.categories.length > 0 ? (
                    <div className="space-y-4">
                      {preview.categories
                        .filter(category => category.items && category.items.length > 0)
                        .map((category) => (
                        <div key={category.categoryId} className="border border-gray-200 rounded-lg">
                          <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                            <h5 className="font-medium text-gray-900">{category.categoryName}</h5>
                          </div>
                          <div className="divide-y divide-gray-100">
                            {category.items && category.items.map((item, idx) => (
                              <div key={idx} className="px-4 py-3 hover:bg-gray-50">
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-900">{item.itemName}</p>
                                    {item.description && (
                                      <p className="text-xs text-gray-600 mt-1">{item.description}</p>
                                    )}
                                    {item.dueDate && (
                                      <p className="text-xs text-orange-600 mt-1 flex items-center">
                                        <CalendarIcon className="h-3 w-3 mr-1" />
                                        Due: {new Date(item.dueDate + 'Z').toLocaleDateString()}
                                      </p>
                                    )}

                                    {/* PIC Selection */}
                                    <div className="flex items-center mt-2 space-x-2">
                                      <span className="text-xs text-gray-600 font-medium">Assignee:</span>
                                      <select
                                        value={
                                          item.teamId ? `team-${item.teamId}` :
                                          item.picId ? `person-${item.picId}` :
                                          ''
                                        }
                                        onChange={(e) => handleUpdatePIC(item.id!, category.categoryId, e.target.value)}
                                        className="text-xs border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                      >
                                        <option value="">Select assignment...</option>
                                        <optgroup label="─── Teams ───">
                                          {teams.map((team) => (
                                            <option key={`team-${team.id}`} value={`team-${team.id}`}>
                                              👥 {team.name}
                                            </option>
                                          ))}
                                        </optgroup>
                                        <optgroup label="─── Persons ───">
                                          {users.map((user) => (
                                            <option key={`person-${user.id}`} value={`person-${user.id}`}>
                                              👤 {user.userName}
                                            </option>
                                          ))}
                                        </optgroup>
                                      </select>
                                      {(item.assignedTeamName || item.assignedPersonName) && (
                                        <span className={`text-xs px-2 py-1 rounded-full ${
                                          item.assignedTeamName ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                                        }`}>
                                          {item.assignedTeamName ? `👥 ${item.assignedTeamName}` : `👤 ${item.assignedPersonName}`}
                                        </span>
                                      )}
                                    </div>

                                    {/* Item-Level Approval */}
                                    <div className="mt-3 space-y-2">
                                      <label className="flex items-center space-x-2">
                                        <input
                                          type="checkbox"
                                          checked={item.requiresApproval || false}
                                          onChange={(e) => handleUpdateItemApproval(item.id!, category.categoryId, e.target.checked, item.approverEmail)}
                                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        />
                                        <span className="text-xs text-gray-700 font-medium">Requires Approval</span>
                                      </label>
                                      {item.requiresApproval && (
                                        <div>
                                          <input
                                            type="email"
                                            value={item.approverEmail || ''}
                                            onChange={(e) => handleUpdateItemApproval(item.id!, category.categoryId, true, e.target.value)}
                                            placeholder="Enter single approver email"
                                            className={`w-full text-xs border rounded px-3 py-2 focus:ring-2 focus:border-transparent ${
                                              approverEmailErrors[item.id!]
                                                ? 'border-red-500 focus:ring-red-500'
                                                 : 'border-gray-300 focus:ring-blue-500'
                                            }`}
                                          />
                                          {approverEmailErrors[item.id!] ? (
                                            <p className="text-xs text-red-600 mt-1">{approverEmailErrors[item.id!]}</p>
                                          ) : (
                                            <p className="text-xs text-gray-500 mt-1">Only one email address allowed</p>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveTask(category.categoryId, item.id!)}
                                    className="ml-4 p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                                    title="Remove task"
                                  >
                                    <XMarkIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">Loading preview...</p>
              </div>
            )}

            <div className="flex justify-between items-center pt-6 border-t">
              <button
                type="button"
                onClick={() => {
                  // Just clear preview and go back
                  setPreview(null);
                  setCurrentStep(2);
                }}
                className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 inline-flex items-center"
                disabled={submitting}
              >
                <ArrowLeftIcon className="mr-2 h-4 w-4" />
                Back
              </button>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  disabled={submitting}
                  className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Save Draft
                </button>
                <button
                  type="button"
                  onClick={handleFinalSubmit}
                  disabled={submitting}
                  className="px-6 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
                >
                  {submitting ? 'Creating...' : (
                    <>
                      Create
                      <CheckCircleIcon className="ml-2 h-5 w-5" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add Task Modal */}
      <AddTaskModal
        isOpen={showAddTaskModal}
        onClose={() => setShowAddTaskModal(false)}
        onSave={handleAddTaskFromModal}
        title="Add Task"
      />
    </div>,
    document.body
  );
}
